// Types
export interface DiaryEntry {
  date: string; // YYYY-MM-DD
  mood: 1 | 2 | 3 | 4 | 5;
  text: string;
  createdAt: string;
  updatedAt: string;
}

export interface DiaryData {
  entries: Record<string, DiaryEntry>;
  version: number;
}

const STORAGE_KEY = "mood-diary-data";
const DATA_VERSION = 1;

// Mood definitions
export const MOODS = [
  { value: 1 as const, emoji: "😢", label: "Ужасно", color: "var(--mood-1)", softColor: "var(--mood-1-soft)" },
  { value: 2 as const, emoji: "😕", label: "Плохо", color: "var(--mood-2)", softColor: "var(--mood-2-soft)" },
  { value: 3 as const, emoji: "😐", label: "Нормально", color: "var(--mood-3)", softColor: "var(--mood-3-soft)" },
  { value: 4 as const, emoji: "🙂", label: "Хорошо", color: "var(--mood-4)", softColor: "var(--mood-4-soft)" },
  { value: 5 as const, emoji: "😄", label: "Отлично", color: "var(--mood-5)", softColor: "var(--mood-5-soft)" },
] as const;

// Russian month names
export const MONTH_NAMES = [
  "Январь", "Февраль", "Март", "Апрель", "Май", "Июнь",
  "Июль", "Август", "Сентябрь", "Октябрь", "Ноябрь", "Декабрь",
];

export const MONTH_NAMES_GENITIVE = [
  "января", "февраля", "марта", "апреля", "мая", "июня",
  "июля", "августа", "сентября", "октября", "ноября", "декабря",
];

export const DAY_NAMES_SHORT = ["Пн", "Вт", "Ср", "Чт", "Пт", "Сб", "Вс"];

// Demo data for statistics when user has < 3 entries
export function getDemoEntries(): DiaryEntry[] {
  const today = new Date();
  const entries: DiaryEntry[] = [];
  const demoMoods: (1 | 2 | 3 | 4 | 5)[] = [3, 4, 5, 3, 4, 2, 5, 4, 3, 5, 4, 3, 2, 4];
  const demoTexts = [
    "Обычный рабочий день, ничего особенного.",
    "Встретился с друзьями после работы, было весело!",
    "Отличный выходной — гулял в парке, читал книгу.",
    "Немного устал на работе, но в целом нормально.",
    "Приготовил новое блюдо, получилось вкусно!",
    "Не выспался, весь день был рассеянным.",
    "Получил похвалу от руководства, день задался!",
    "Хороший продуктивный день.",
    "Ходил в кино, фильм так себе.",
    "Замечательная прогулка у реки на закате.",
    "Закончил интересный проект на работе.",
    "Провёл день дома, отдыхал и смотрел сериал.",
    "Поссорился с другом, настроение ни к чёрту.",
    "Занимался спортом, чувствую прилив энергии!",
  ];

  for (let i = 13; i >= 0; i--) {
    const date = new Date(today);
    date.setDate(date.getDate() - i);
    const dateStr = formatDate(date);
    entries.push({
      date: dateStr,
      mood: demoMoods[13 - i],
      text: demoTexts[13 - i],
      createdAt: date.toISOString(),
      updatedAt: date.toISOString(),
    });
  }
  return entries;
}

// Helpers
export function formatDate(date: Date): string {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
}

export function parseDate(dateStr: string): Date {
  const [y, m, d] = dateStr.split("-").map(Number);
  return new Date(y, m - 1, d);
}

export function formatDateHuman(dateStr: string): string {
  const date = parseDate(dateStr);
  return `${date.getDate()} ${MONTH_NAMES_GENITIVE[date.getMonth()]} ${date.getFullYear()}`;
}

export function isToday(dateStr: string): boolean {
  return dateStr === formatDate(new Date());
}

// Storage functions
export function loadData(): DiaryData {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (raw) {
      const parsed = JSON.parse(raw);
      if (parsed && parsed.version === DATA_VERSION) {
        return parsed;
      }
    }
  } catch {
    // corrupt data, start fresh
  }
  return { entries: {}, version: DATA_VERSION };
}

export function saveData(data: DiaryData): void {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(data));
}

export function getEntry(date: string): DiaryEntry | null {
  const data = loadData();
  return data.entries[date] || null;
}

export function saveEntry(entry: DiaryEntry): void {
  const data = loadData();
  data.entries[entry.date] = entry;
  saveData(data);
}

export function deleteEntry(date: string): void {
  const data = loadData();
  delete data.entries[date];
  saveData(data);
}

export function getAllEntries(): DiaryEntry[] {
  const data = loadData();
  return Object.values(data.entries).sort((a, b) => a.date.localeCompare(b.date));
}

export function getEntriesForMonth(year: number, month: number): Record<string, DiaryEntry> {
  const data = loadData();
  const prefix = `${year}-${String(month + 1).padStart(2, "0")}`;
  const result: Record<string, DiaryEntry> = {};
  for (const [key, entry] of Object.entries(data.entries)) {
    if (key.startsWith(prefix)) {
      result[key] = entry;
    }
  }
  return result;
}

export function exportData(): string {
  const data = loadData();
  return JSON.stringify(data, null, 2);
}

export function importData(jsonStr: string): { success: boolean; message: string; count: number } {
  try {
    const parsed = JSON.parse(jsonStr);
    if (!parsed || typeof parsed !== "object") {
      return { success: false, message: "Неверный формат файла", count: 0 };
    }
    if (!parsed.entries || typeof parsed.entries !== "object") {
      return { success: false, message: "В файле отсутствуют записи", count: 0 };
    }
    const count = Object.keys(parsed.entries).length;
    parsed.version = DATA_VERSION;
    saveData(parsed as DiaryData);
    return { success: true, message: `Импортировано записей: ${count}`, count };
  } catch {
    return { success: false, message: "Ошибка чтения файла. Убедитесь, что это корректный JSON.", count: 0 };
  }
}

// Calendar helpers
export function getDaysInMonth(year: number, month: number): number {
  return new Date(year, month + 1, 0).getDate();
}

export function getFirstDayOfMonth(year: number, month: number): number {
  // Returns 0=Mon, 1=Tue, ..., 6=Sun
  const day = new Date(year, month, 1).getDay();
  return day === 0 ? 6 : day - 1;
}
